import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { signupUser } from "../features/auth/authSlice";
import {
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  Paper,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import PersonIcon from "@mui/icons-material/Person";
import { useNavigate } from "react-router-dom";

function SignUp({ onClose, onSwitchToSignIn }) {
  const dispatch = useDispatch();
  const { loading, error } = useSelector((state) => state.auth);
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();

  const [form, setForm] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    firstName: "",
    lastName: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  const colors = {
    primary: "#e50914",
    secondary: "#6a0dad",
    accent: "#A380FF",
  };

  const renderError = () => {
    if (!error) return null;
    if (typeof error === "string") return error;
    
    let errorDetail = error.detail;
    if (!errorDetail && Array.isArray(error)) {
      errorDetail = error;
    }
    
    if (Array.isArray(errorDetail)) {
      return (
        <Box component="ul" sx={{ m: 0, pl: 2, textAlign: "left" }}>
          {errorDetail.map((err, idx) => {
            const field = err.loc && err.loc.length > 0 ? err.loc[err.loc.length - 1] : "Field";
            const fieldName = field.charAt(0).toUpperCase() + field.slice(1);
            return (
              <Box component="li" key={idx}>
                <strong>{fieldName}</strong>: {err.msg}
              </Box>
            );
          })}
        </Box>
      );
    }
    
    if (typeof errorDetail === "string") {
      return errorDetail;
    }
    
    return "Something went wrong. Please try again.";
  };

  const validateForm = () => {
    const errors = {};

    if (!form.username.trim()) {
      errors.username = "Username is required";
    } else if (form.username.length < 3) {
      errors.username = "Username must be at least 3 characters";
    }

    if (!form.email.trim()) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      errors.email = "Please enter a valid email address";
    }

    if (!form.password) {
      errors.password = "Password is required";
    } else if (form.password.length < 6) {
      errors.password = "Password must be at least 6 characters";
    }

    if (!form.confirmPassword) {
      errors.confirmPassword = "Please confirm your password";
    } else if (form.password !== form.confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }

    if (!form.firstName.trim()) {
      errors.firstName = "First name is required";
    }

    if (!form.lastName.trim()) {
      errors.lastName = "Last name is required";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors({ ...formErrors, [name]: "" });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    // Prepare data for backend (only send required fields for now)
    const signupData = {
      username: form.username,
      email: form.email,
      password: form.password,
      // firstName and lastName can be added when backend supports them
    };

    dispatch(signupUser(signupData)).then((res) => {
      if (res.meta.requestStatus === "fulfilled") {
        if (onClose) onClose();
        if (onSwitchToSignIn) onSwitchToSignIn();
      }
    });
  };

  return (
    <Paper
      elevation={0}
      sx={{
        position: "relative",
        p: { xs: 3, sm: 4, md: 5 },
        maxWidth: 500,
        mx: "auto",
        background: isDark
          ? "linear-gradient(135deg, rgba(26, 26, 26, 0.95) 0%, rgba(30, 30, 30, 0.95) 100%)"
          : "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(250, 250, 250, 0.95) 100%)",
        backdropFilter: "blur(10px)",
        borderRadius: "24px",
        border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
      }}
    >
      {/* Close button */}
      {onClose && (
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            color: theme.palette.text.secondary,
            "&:hover": { color: colors.primary },
          }}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      )}

      {/* Header */}
      <Box sx={{ textAlign: "center", mb: 4 }}>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 64,
            height: 64,
            borderRadius: "50%",
            background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
            mb: 2,
          }}
        >
          <PersonAddIcon sx={{ fontSize: 32, color: "#fff" }} />
        </Box>
        <Typography
          variant="h4"
          fontWeight={900}
          sx={{
            mb: 1,
            background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Create Account
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Join us and start your coding journey today
        </Typography>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            borderRadius: "12px",
            "& .MuiAlert-icon": { color: colors.primary },
          }}
        >
          {renderError()}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Stack spacing={2.5}>
          {/* First Name and Last Name Row */}
          <Stack direction="row" spacing={2}>
            <TextField
              label="First Name"
              name="firstName"
              fullWidth
              value={form.firstName}
              onChange={handleChange}
              required
              error={!!formErrors.firstName}
              helperText={formErrors.firstName}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon sx={{ color: colors.primary }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  "&:hover fieldset": {
                    borderColor: colors.primary,
                  },
                },
              }}
            />
            <TextField
              label="Last Name"
              name="lastName"
              fullWidth
              value={form.lastName}
              onChange={handleChange}
              required
              error={!!formErrors.lastName}
              helperText={formErrors.lastName}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon sx={{ color: colors.secondary }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  "&:hover fieldset": {
                    borderColor: colors.secondary,
                  },
                },
              }}
            />
          </Stack>

          {/* Username */}
          <TextField
            label="Username"
            name="username"
            fullWidth
            value={form.username}
            onChange={handleChange}
            required
            error={!!formErrors.username}
            helperText={formErrors.username || "At least 3 characters"}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon sx={{ color: colors.accent }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                "&:hover fieldset": {
                  borderColor: colors.accent,
                },
              },
            }}
          />

          {/* Email */}
          <TextField
            label="Email Address"
            name="email"
            type="email"
            fullWidth
            value={form.email}
            onChange={handleChange}
            required
            error={!!formErrors.email}
            helperText={formErrors.email}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon sx={{ color: colors.primary }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                "&:hover fieldset": {
                  borderColor: colors.primary,
                },
              },
            }}
          />

          {/* Password */}
          <TextField
            label="Password"
            name="password"
            type={showPassword ? "text" : "password"}
            fullWidth
            value={form.password}
            onChange={handleChange}
            required
            error={!!formErrors.password}
            helperText={formErrors.password || "At least 6 characters"}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon sx={{ color: colors.secondary }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                "&:hover fieldset": {
                  borderColor: colors.secondary,
                },
              },
            }}
          />

          {/* Confirm Password */}
          <TextField
            label="Confirm Password"
            name="confirmPassword"
            type={showConfirmPassword ? "text" : "password"}
            fullWidth
            value={form.confirmPassword}
            onChange={handleChange}
            required
            error={!!formErrors.confirmPassword}
            helperText={formErrors.confirmPassword}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon sx={{ color: colors.accent }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                "&:hover fieldset": {
                  borderColor: colors.accent,
                },
              },
            }}
          />

          {/* Submit Button */}
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading}
            sx={{
              mt: 2,
              py: 1.5,
              borderRadius: "12px",
              fontSize: "1rem",
              fontWeight: 700,
              background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
              boxShadow: `0 4px 16px ${colors.primary}40`,
              transition: "all 0.3s ease",
              "&:hover": {
                background: `linear-gradient(135deg, #ff1a3c, #8e24aa)`,
                boxShadow: `0 8px 24px ${colors.primary}60`,
                transform: "translateY(-2px)",
              },
              "&:disabled": {
                background: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
              },
            }}
          >
            {loading ? (
              <CircularProgress size={24} sx={{ color: "#fff" }} />
            ) : (
              "Create Account"
            )}
          </Button>
        </Stack>
      </form>

      {/* Sign In Link */}
      <Box sx={{ textAlign: "center", mt: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Already have an account?{" "}
          <Link
            component="button"
            type="button"
            onClick={() => {
              if (onClose) onClose();
              if (onSwitchToSignIn) {
                onSwitchToSignIn();
              } else {
                navigate("/signin");
              }
            }}
            sx={{
              color: colors.primary,
              fontWeight: 600,
              textDecoration: "none",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            Sign In
          </Link>
        </Typography>
      </Box>
    </Paper>
  );
}

export default SignUp;
